import type {MRT_Cell, MRT_ColumnDef} from 'mantine-react-table';
import {useMemo} from 'react';
import type {
  PersonnelSimpleResponse,
  UserStatusEnum,
} from 'src/api/ProactApiClient';
import {Pill} from 'src/design/atoms/Pill';
import {
  NoYesPills,
  UserBannedPill,
  UserIneligiblePill,
  UserLeaveOfAbsencePill,
  UserStatePills,
  UserSuspendedPill,
} from 'src/shared/constants/PillConstants';
import {ProfileAvatar} from 'src/app/personnel/ProfileAvatar';
import {
  UserStateDataOptions,
  YesNoStateDataOptions,
} from 'src/shared/constants/columnFilterData';
import {serverComputedColumn} from 'src/design/organisms/serverComputedColumn';

interface UseVendorPersonnelColumnsProps {
  disableActiveFilter?: boolean;
}

export const useVendorPersonnelColumns = ({
  disableActiveFilter = false,
}: UseVendorPersonnelColumnsProps = {}) => {
  const columns = useMemo<MRT_ColumnDef<PersonnelSimpleResponse>[]>(
    () => [
      {
        accessorKey: 'active',
        header: 'Active',
        enableColumnFilter: !disableActiveFilter,
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: UserStateDataOptions,
        },
        size: 110,
        Cell: ({cell}: {cell: MRT_Cell<PersonnelSimpleResponse, unknown>}) => (
          <Pill {...UserStatePills(cell.getValue<UserStatusEnum>())} />
        ),
      },
      {
        accessorKey: 'firstName',
        header: 'First',
        size: 130,
      },
      {
        accessorKey: 'lastName',
        header: 'Last',
        size: 130,
      },
      {
        accessorKey: 'avatarUrl',
        header: '',
        enableColumnFilterModes: false,
        enableColumnFilter: false,
        enableSorting: false,
        enableClickToCopy: false,
        enableColumnActions: false,
        enableResizing: false,
        size: 44,
        Cell: ({cell}) => (
          <ProfileAvatar alt={'Avatar Image'} src={cell.getValue<string>()} />
        ),
      },
      serverComputedColumn({
        accessorKey: 'hasOpenTraining',
        header: 'Open Training',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select' as const,
        mantineFilterSelectProps: {
          data: YesNoStateDataOptions,
        },
        Cell: ({cell}: {cell: MRT_Cell<PersonnelSimpleResponse, unknown>}) => (
          <Pill {...NoYesPills(cell.getValue<boolean | undefined>())} />
        ),
        filterable: true,
      }),
      {
        accessorKey: 'vendor.id',
        header: 'Vendor ID',
      },
      {
        accessorKey: 'vendor.name',
        header: 'Vendor',
      },
      {
        id: 'createdAt',
        accessorFn: row => (row.createdAt ? new Date(row.createdAt) : null),
        filterVariant: 'date',
        header: 'Created Date',
        Cell: ({cell}: {cell: MRT_Cell<PersonnelSimpleResponse, unknown>}) => (
          <>{cell.getValue<string>()?.toString()?.toShortDate()}</>
        ),
      },
      {
        id: 'updatedAt',
        accessorFn: row => (row.updatedAt ? new Date(row.updatedAt) : null),
        filterVariant: 'date',
        header: 'Updated Date',
        Cell: ({cell}: {cell: MRT_Cell<PersonnelSimpleResponse, unknown>}) => (
          <>{cell.getValue<string>()?.toString()?.toShortDate()}</>
        ),
      },
      {
        accessorKey: 'employeeId',
        header: 'Employee ID',
      },
      {
        accessorKey: 'email',
        header: 'Email',
      },
      {
        accessorKey: 'supervisor.name',
        header: 'Supervisor',
      },
      {
        accessorKey: 'isSuspended',
        header: 'Suspended',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: YesNoStateDataOptions,
        },
        maxSize: 150,
        Cell: ({cell}: {cell: MRT_Cell<PersonnelSimpleResponse, unknown>}) => (
          <>
            {cell.getValue<boolean | undefined>() && (
              <Pill {...UserSuspendedPill} />
            )}
          </>
        ),
      },
      {
        accessorKey: 'isLeaveOfAbsence',
        header: 'LOA',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: YesNoStateDataOptions,
        },
        maxSize: 150,
        Cell: ({cell}: {cell: MRT_Cell<PersonnelSimpleResponse, unknown>}) => (
          <>
            {cell.getValue<boolean | undefined>() && (
              <Pill {...UserLeaveOfAbsencePill} />
            )}
          </>
        ),
      },
      {
        accessorKey: 'isEligible',
        header: 'Eligibility',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: YesNoStateDataOptions,
        },
        maxSize: 150,
        Cell: ({cell}: {cell: MRT_Cell<PersonnelSimpleResponse, unknown>}) => (
          <>
            {cell.getValue<boolean | undefined>() === false && (
              <Pill {...UserIneligiblePill} />
            )}
          </>
        ),
      },
      {
        accessorKey: 'isBannedOnProperty',
        header: 'Banned on Property',
        enableColumnFilterModes: false,
        filterVariant: 'multi-select',
        mantineFilterSelectProps: {
          data: YesNoStateDataOptions,
        },
        maxSize: 150,
        Cell: ({cell}: {cell: MRT_Cell<PersonnelSimpleResponse, unknown>}) => (
          <>
            {cell.getValue<boolean | undefined>() && (
              <Pill {...UserBannedPill} />
            )}
          </>
        ),
      },
      {
        accessorKey: 'jobCode',
        header: 'Job Code',
      },
      {
        accessorKey: 'jobTitle',
        header: 'Job Title',
      },
    ],
    [disableActiveFilter]
  );

  return columns;
};
